import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
export function MainContent() {
  const {
    currentEra
  } = useTheme();
  // Era-specific main content styles
  const getMainContentStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'relative z-10 bg-white p-6 border-l-4 border-black';
      case '70s':
        return 'relative z-10 bg-amber-50 p-6 border-l-4 border-amber-800 rounded-lg';
      case '80s':
        return 'relative z-10 bg-gradient-to-br from-indigo-900 to-black p-6 border-l-4 border-cyan-400';
      case '90s':
        return 'relative z-10 bg-gradient-to-br from-gray-900 to-black p-6 border-l-4 border-cyan-400';
      case '2000s':
        return 'relative z-10 bg-white p-6 border-l-4 border-blue-600 rounded shadow-md';
      default:
        return 'relative z-10 bg-gradient-to-br from-gray-900 to-black p-6 border-l-4 border-cyan-400';
    }
  };
  // Era-specific headline styles
  const getHeadlineStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'text-3xl md:text-4xl font-bold tracking-tight uppercase text-black';
      case '70s':
        return 'text-3xl md:text-4xl font-bold tracking-wide uppercase text-amber-800';
      case '80s':
        return 'text-3xl md:text-4xl font-bold tracking-tight uppercase text-white';
      case '90s':
        return 'text-3xl md:text-4xl font-bold tracking-tight uppercase text-white';
      case '2000s':
        return 'text-3xl md:text-4xl font-bold tracking-normal uppercase text-blue-600';
      default:
        return 'text-3xl md:text-4xl font-bold tracking-tight uppercase text-white';
    }
  };
  // Era-specific breaking label
  const getBreakingLabel = () => {
    switch (currentEra) {
      case '60s':
        return <span className="bg-black text-white text-xs font-bold uppercase px-2 py-1 mr-2" data-id="element-148">
            Breaking
          </span>;
      case '70s':
        return <span className="bg-amber-800 text-white text-xs font-bold uppercase px-2 py-1 mr-2 rounded-full" data-id="element-149">
            Breaking
          </span>;
      case '80s':
        return <span className="bg-pink-500 text-black text-xs font-bold uppercase px-2 py-1 mr-2" style={{
          clipPath: 'polygon(0 0, 100% 0, 90% 100%, 10% 100%)'
        }} data-id="element-150">
            Breaking
          </span>;
      case '90s':
        return <span className="bg-pink-500 text-black text-xs font-bold uppercase px-2 py-1 mr-2" data-id="element-151">
            Breaking
          </span>;
      case '2000s':
        return <span className="bg-orange-500 text-white text-xs font-bold uppercase px-2 py-1 mr-2 rounded" data-id="element-152">
            Breaking
          </span>;
      default:
        return <span className="bg-pink-500 text-black text-xs font-bold uppercase px-2 py-1 mr-2" data-id="element-153">
            Breaking
          </span>;
    }
  };
  // Era-specific event box styles
  const getEventBoxStyles = (type: 'first' | 'second') => {
    switch (currentEra) {
      case '60s':
        return type === 'first' ? 'bg-black text-white p-4' : 'bg-black text-white p-4';
      case '70s':
        return type === 'first' ? 'bg-amber-800 text-white p-4 rounded-lg' : 'bg-amber-600 text-white p-4 rounded-lg';
      case '80s':
        return type === 'first' ? 'bg-cyan-400 text-black p-4' : 'bg-pink-500 text-black p-4';
      case '90s':
        return type === 'first' ? 'bg-cyan-400 text-black p-4' : 'bg-pink-500 text-black p-4';
      case '2000s':
        return type === 'first' ? 'bg-blue-600 text-white p-4 rounded shadow' : 'bg-orange-500 text-white p-4 rounded shadow';
      default:
        return type === 'first' ? 'bg-cyan-400 text-black p-4' : 'bg-pink-500 text-black p-4';
    }
  };
  // Era-specific button styles
  const getButtonStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'flex items-center gap-2 bg-black text-white px-4 py-2 font-bold uppercase text-sm';
      case '70s':
        return 'flex items-center gap-2 bg-amber-600 text-white px-4 py-2 font-bold uppercase text-sm rounded-full';
      case '80s':
        return 'flex items-center gap-2 bg-yellow-400 text-black px-4 py-2 font-bold uppercase text-sm';
      case '90s':
        return 'flex items-center gap-2 bg-yellow-400 text-black px-4 py-2 font-bold uppercase text-sm';
      case '2000s':
        return 'flex items-center gap-2 bg-blue-600 text-white px-4 py-2 font-bold uppercase text-sm rounded shadow hover:bg-blue-700 transition-colors';
      default:
        return 'flex items-center gap-2 bg-yellow-400 text-black px-4 py-2 font-bold uppercase text-sm';
    }
  };
  // Era-specific inside box styles
  const getInsideBoxStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'border-2 border-black p-4 mb-4';
      case '70s':
        return 'border-2 border-amber-800 p-4 mb-4 rounded-lg';
      case '80s':
        return 'border-2 border-dashed border-pink-500 p-4 mb-4';
      case '90s':
        return 'border-2 border-dashed border-pink-500 p-4 mb-4';
      case '2000s':
        return 'border-2 border-blue-600 p-4 mb-4 rounded shadow-sm';
      default:
        return 'border-2 border-dashed border-pink-500 p-4 mb-4';
    }
  };
  // Era-specific quote box styles
  const getQuoteBoxStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'bg-black text-white p-4';
      case '70s':
        return 'bg-gradient-to-r from-amber-800 to-amber-600 p-4 rounded-lg';
      case '80s':
        return 'bg-gradient-to-r from-purple-600 to-pink-500 p-4';
      case '90s':
        return 'bg-gradient-to-r from-purple-600 to-pink-500 p-4';
      case '2000s':
        return 'bg-gradient-to-r from-blue-600 to-orange-500 p-4 rounded shadow';
      default:
        return 'bg-gradient-to-r from-purple-600 to-pink-500 p-4';
    }
  };
  // Era-specific bullet point styles
  const getBulletPointStyles = (color: string) => {
    switch (currentEra) {
      case '60s':
        return 'text-black';
      case '70s':
        return color === 'cyan' ? 'text-amber-800' : color === 'pink' ? 'text-amber-600' : color === 'yellow' ? 'text-amber-700' : 'text-amber-800';
      case '80s':
        return color === 'cyan' ? 'text-cyan-400' : color === 'pink' ? 'text-pink-500' : color === 'yellow' ? 'text-yellow-400' : 'text-purple-600';
      case '90s':
        return color === 'cyan' ? 'text-cyan-400' : color === 'pink' ? 'text-pink-500' : color === 'yellow' ? 'text-yellow-400' : 'text-purple-600';
      case '2000s':
        return color === 'cyan' ? 'text-blue-600' : color === 'pink' ? 'text-orange-500' : color === 'yellow' ? 'text-green-500' : 'text-purple-600';
      default:
        return color === 'cyan' ? 'text-cyan-400' : color === 'pink' ? 'text-pink-500' : color === 'yellow' ? 'text-yellow-400' : 'text-purple-600';
    }
  };
  // Era-specific geometric element
  const getGeometricElement = () => {
    switch (currentEra) {
      case '60s':
        return <div className="hidden md:block absolute -top-8 -right-8 w-20 h-20 bg-black rotate-12 z-0" data-id="element-154"></div>;
      case '70s':
        return <div className="hidden md:block absolute -top-8 -right-8 w-20 h-20 bg-amber-800 rounded-full z-0" data-id="element-155"></div>;
      case '80s':
        return <div className="hidden md:block absolute -top-8 -right-8 w-20 h-20 bg-purple-600 rotate-12 z-0" data-id="element-156"></div>;
      case '90s':
        return <div className="hidden md:block absolute -top-8 -right-8 w-20 h-20 bg-purple-600 rotate-12 z-0" data-id="element-157"></div>;
      case '2000s':
        return <div className="hidden md:block absolute -top-8 -right-8 w-20 h-20 bg-gradient-to-r from-blue-600 to-orange-500 rounded-lg shadow z-0" data-id="element-158"></div>;
      default:
        return <div className="hidden md:block absolute -top-8 -right-8 w-20 h-20 bg-purple-600 rotate-12 z-0" data-id="element-159"></div>;
    }
  };
  return <main className="col-span-1 md:col-span-8 relative" data-id="element-160">
      {/* Geometric element */}
      {getGeometricElement()}
      {/* Main article */}
      <div className={getMainContentStyles()} data-id="element-161">
        <div className="flex items-center mb-4" data-id="element-162">
          {getBreakingLabel()}
          <h2 className={getHeadlineStyles()} data-id="element-163">
            The Revolution Will Not Be Televised
          </h2>
        </div>
        <div className="flex flex-col md:flex-row gap-6" data-id="element-164">
          <div className="md:w-2/3" data-id="element-165">
            <p className={`text-xl font-bold mb-4 ${currentEra === '60s' ? 'text-black' : currentEra === '70s' ? 'text-amber-800' : currentEra === '80s' ? 'text-yellow-400' : currentEra === '90s' ? 'text-yellow-400' : 'text-orange-500'}`} data-id="element-166">
              How underground zines are changing the landscape of queer activism
            </p>
            <div className="mb-6 relative" data-id="element-167">
              <div className={`absolute -left-4 top-0 bottom-0 w-1 ${currentEra === '60s' ? 'bg-black' : currentEra === '70s' ? 'bg-amber-800' : currentEra === '80s' ? 'bg-pink-500' : currentEra === '90s' ? 'bg-pink-500' : 'bg-orange-500'}`} data-id="element-168"></div>
              <p className={`text-lg mb-4 ${currentEra === '60s' ? 'text-black' : currentEra === '70s' ? 'text-amber-900' : currentEra === '80s' ? 'text-white' : currentEra === '90s' ? 'text-white' : 'text-gray-700'}`} data-id="element-169">
                In the face of mainstream media silence, queer activists are
                taking matters into their own hands. The resurgence of DIY print
                culture is giving voice to those who have been systematically
                silenced.
              </p>
              <p className={`text-lg mb-4 ${currentEra === '60s' ? 'text-black' : currentEra === '70s' ? 'text-amber-900' : currentEra === '80s' ? 'text-white' : currentEra === '90s' ? 'text-white' : 'text-gray-700'}`} data-id="element-170">
                "We don't need permission to exist," says activist Jamie Rivera.
                "These zines are our megaphones, our protest banners, our
                community bulletin boards all in one."
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-171">
              <div className={getEventBoxStyles('first')} data-id="element-172">
                <p className="font-bold uppercase text-sm tracking-wider" data-id="element-173">
                  Protest Planning
                </p>
                <p className="text-xs mt-1" data-id="element-174">City Hall • July 15 • 2PM</p>
              </div>
              <div className={`${getEventBoxStyles('second')} cursor-pointer hover:opacity-90 transition-opacity`} onClick={() => {
              // Find the parent component that has the setCurrentView function
              // and call it with 'workshop'
              if (typeof window !== 'undefined') {
                const workshopButton = document.querySelector('button[onClick*="workshop"]');
                if (workshopButton) {
                  workshopButton.click();
                }
              }
            }} data-id="element-175">
                <p className="font-bold uppercase text-sm tracking-wider" data-id="element-176">
                  Zine Workshop
                </p>
                <p className="text-xs mt-1" data-id="element-177">Community Center • Weekly</p>
              </div>
            </div>
            <button className={getButtonStyles()} data-id="element-178">
              Read Full Story <ArrowRightIcon className="h-4 w-4" data-id="element-179" />
            </button>
          </div>
          <div className="md:w-1/3" data-id="element-180">
            <div className={getInsideBoxStyles()} data-id="element-181">
              <h3 className={`text-xl font-bold uppercase mb-2 ${currentEra === '60s' ? 'text-black' : currentEra === '70s' ? 'text-amber-800' : currentEra === '80s' ? 'text-cyan-400' : currentEra === '90s' ? 'text-cyan-400' : 'text-blue-600'}`} data-id="element-182">
                Inside This Issue
              </h3>
              <ul className="space-y-2" data-id="element-183">
                <li className="flex items-center gap-2" data-id="element-184">
                  <span className={getBulletPointStyles('cyan')} data-id="element-185">▶</span>
                  <span className={currentEra === '60s' ? 'text-black' : currentEra === '70s' ? 'text-amber-900' : currentEra === '80s' ? 'text-white' : currentEra === '90s' ? 'text-white' : 'text-gray-700'} data-id="element-186">
                    Protest Fashion: Wear Your Message
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-187">
                  <span className={getBulletPointStyles('pink')} data-id="element-188">▶</span>
                  <span className={currentEra === '60s' ? 'text-black' : currentEra === '70s' ? 'text-amber-900' : currentEra === '80s' ? 'text-white' : currentEra === '90s' ? 'text-white' : 'text-gray-700'} data-id="element-189">
                    DIY Activism: Start Your Own Zine
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-190">
                  <span className={getBulletPointStyles('yellow')} data-id="element-191">▶</span>
                  <span className={currentEra === '60s' ? 'text-black' : currentEra === '70s' ? 'text-amber-900' : currentEra === '80s' ? 'text-white' : currentEra === '90s' ? 'text-white' : 'text-gray-700'} data-id="element-192">
                    Community Calendar: July Events
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-193">
                  <span className={getBulletPointStyles('purple')} data-id="element-194">▶</span>
                  <span className={currentEra === '60s' ? 'text-black' : currentEra === '70s' ? 'text-amber-900' : currentEra === '80s' ? 'text-white' : currentEra === '90s' ? 'text-white' : 'text-gray-700'} data-id="element-195">
                    Artist Spotlight: Radical Posters
                  </span>
                </li>
              </ul>
            </div>
            <div className={getQuoteBoxStyles()} data-id="element-196">
              <p className="text-2xl font-bold uppercase text-center" data-id="element-197">
                "Silence = Death"
              </p>
              <p className="text-xs text-center mt-2" data-id="element-198">- ACT UP, 1987</p>
            </div>
          </div>
        </div>
      </div>
    </main>;
}